import { FULL_TEXT_SEARCH_API_URL } from 'pages/APIs';
import AbstractApiService from 'src/services/abstractApiService';

export default class GraphExplorerApiService extends AbstractApiService {
  private get baseUrl(): string {
    return FULL_TEXT_SEARCH_API_URL + '/opensearch/graph';
  }

  public constructor() {
    super({});
  }

  public async getAllGraph(limit: number = 5000): Promise<any> {
    return super.get(`${this.baseUrl}/all?limit=${limit}`);
  }

  public async getVendorHubGraph(topN: number = 20): Promise<any> {
    return super.get(`${this.baseUrl}/vendor-hub?top_n=${topN}`);
  }

  public async getNodeSubgraph(
    nodeId: string,
    depth: number = 1,
    edgeTypes?: string,
    minConfidence: number = 0,
  ): Promise<any> {
    let url = `${this.baseUrl}/node/${nodeId}?depth=${depth}&min_confidence=${minConfidence}`;
    if (edgeTypes) {
      url += `&edge_types=${edgeTypes}`;
    }
    return super.get(url);
  }

  public async expandNode(
    nodeId: string,
    depth: number = 2,
    edgeTypes?: string,
    nodeTypes?: string,
    minConfidence: number = 0,
  ): Promise<any> {
    let url = `${this.baseUrl}/node/${nodeId}/expand?depth=${depth}&min_confidence=${minConfidence}`;
    if (edgeTypes) {
      url += `&edge_types=${edgeTypes}`;
    }
    if (nodeTypes) {
      url += `&node_types=${nodeTypes}`;
    }
    return super.get(url);
  }

  public async searchGraph(
    query: string,
    nodeTypes?: string[],
    limit: number = 20,
  ): Promise<any> {
    const payload: any = { query, limit };
    if (nodeTypes && nodeTypes.length > 0) {
      payload.node_types = nodeTypes;
    }
    return super.post(`${this.baseUrl}/search`, payload);
  }

  public async getGraphStats(): Promise<any> {
    return super.get(`${this.baseUrl}/stats`);
  }

  public async computeMetadataGraph(
    forceRebuild: boolean = false,
    batchSize: number = 100,
  ): Promise<any> {
    return super.post(`${this.baseUrl}/compute/metadata`, {
      force_rebuild: forceRebuild,
      batch_size: batchSize,
    });
  }

  public async getAnomalies(
    anomalyTypes?: string[],
    limit: number = 100,
  ): Promise<any> {
    let url = `${this.baseUrl}/anomalies?limit=${limit}`;
    if (anomalyTypes && anomalyTypes.length > 0) {
      url += `&anomaly_types=${anomalyTypes.join(',')}`;
    }
    return super.get(url);
  }

  public async rebuildGraph(): Promise<any> {
    return super.post(`${this.baseUrl}/rebuild`, {});
  }

  public getComputeProgressStreamURL(orgId: string, token: string): string {
    return `${this.baseUrl}/compute/progress/stream?org_id=${encodeURIComponent(orgId)}&token=${encodeURIComponent(token)}`;
  }

  // === Process Mining ===

  public async getProcessMap(
    docType?: string,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<any> {
    const params = new URLSearchParams();
    if (docType) params.append('doc_type', docType);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    const qs = params.toString();
    return super.get(`${this.baseUrl}/process-map${qs ? '?' + qs : ''}`);
  }

  public async getProcessVariants(
    docType?: string,
    limit: number = 20,
  ): Promise<any> {
    const params = new URLSearchParams();
    if (docType) params.append('doc_type', docType);
    params.append('limit', String(limit));
    return super.get(`${this.baseUrl}/process-variants?${params.toString()}`);
  }

  public async getProcessKpis(docType?: string): Promise<any> {
    const qs = docType ? `?doc_type=${encodeURIComponent(docType)}` : '';
    return super.get(`${this.baseUrl}/process-kpis${qs}`);
  }
}
